import React, { useMemo, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Loader2, Lock } from "lucide-react";
import useConfig from "@/hooks/useConfig";

export interface StripePaymentFormProps {
  /**
   * PaymentIntent / SetupIntent client secret the card form confirms against.
   * The <Elements> provider is mounted in "clientSecret mode", so this must be
   * present before the form renders (the caller fetches it server-side first).
   */
  clientSecret: string;
  /**
   * Where Stripe sends the user for redirect-based methods and 3-D Secure
   * challenges. The common (non-redirect) path resolves inline via onSuccess.
   */
  returnUrl: string;
  /** Fired on a successful confirmation that did NOT need a redirect. */
  onSuccess: (paymentIntent?: any) => void;
  /** Fired when Stripe returns an error, with a user-safe message. */
  onError?: (message: string) => void;
  /**
   * Runs immediately before confirmPayment. Lets callers persist any state they
   * need to rebuild after the 3-D Secure redirect, where the page reloads and
   * in-memory wizard state is lost.
   */
  onBeforeConfirm?: () => void;
  submitLabel: string;
  processingLabel?: string;
  disabled?: boolean;
}

// Only card/validation errors expose Stripe's own message; every other error
// type shows a generic string so internal Stripe error text is never leaked to
// the user. Mirrors CheckoutForm.showStripeFailure in the v2 checkout.
function messageForStripeError(error: any): string {
  return error?.type === "card_error" || error?.type === "validation_error"
    ? (error.message ?? "An unexpected error occurred.")
    : "An unexpected error occurred.";
}

// Assumes it is rendered inside an <Elements> provider so the Stripe hooks are
// available. Kept separate from the provider wrapper below.
function StripePaymentFields({
  returnUrl,
  onSuccess,
  onError,
  onBeforeConfirm,
  submitLabel,
  processingLabel = "Processing...",
  disabled,
}: Omit<StripePaymentFormProps, "clientSecret">) {
  const stripe = useStripe();
  const elements = useElements();
  const [isBusy, setIsBusy] = useState(false);

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setIsBusy(true);
    onBeforeConfirm?.();
    try {
      // clientSecret mode: the secret already lives on the Elements instance,
      // so it is not passed again here and elements.submit() is not required.
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: "if_required",
      });
      if (error) {
        onError?.(messageForStripeError(error));
        return;
      }
      // redirect: "if_required" only resolves here when no redirect was needed;
      // redirect-based methods / 3-D Secure navigate to returnUrl instead. A
      // resolved-without-error result can still be non-final (requires_action /
      // requires_payment_method / canceled), so only treat a truly captured or
      // in-flight charge as success - otherwise the wizard would show "paid"
      // for a payment that never completed.
      const status = paymentIntent?.status;
      if (status === "succeeded" || status === "processing") {
        onSuccess(paymentIntent);
      } else {
        onError?.(
          "Your payment could not be completed. Please try again with a different method.",
        );
      }
    } catch (err: any) {
      onError?.("An unexpected error occurred.");
    } finally {
      setIsBusy(false);
    }
  };

  const busy = disabled || isBusy || !stripe || !elements;

  return (
    <div className="space-y-4">
      <div
        className="rounded-xl border border-border bg-card"
        style={{ padding: "16px" }}
      >
        <PaymentElement options={{ layout: "tabs" }} />
      </div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="w-3 h-3" /> Your payment information is secure and
        encrypted.
      </div>
      <button
        type="button"
        onClick={handlePay}
        disabled={busy}
        className="w-full h-11 rounded-lg text-white font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-50 bg-action hover:bg-action-hover"
      >
        {isBusy ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> {processingLabel}
          </>
        ) : (
          <>
            <Lock className="w-4 h-4" /> {submitLabel}
          </>
        )}
      </button>
    </div>
  );
}

/**
 * Reusable Stripe card form: owns the <Elements> provider (loaded from the
 * tenant's publishable key) plus the PaymentElement and confirm logic, so any
 * page with a client secret can drop in a properly-rendered, consistent payment
 * form. Mirrors the confirm flow already used by the v2 checkout.
 */
export function StripePaymentForm({
  clientSecret,
  ...fieldProps
}: StripePaymentFormProps) {
  const config = useConfig();
  const stripePromise = useMemo(
    () =>
      config?.stripePublishableKey
        ? loadStripe(config.stripePublishableKey)
        : null,
    [config?.stripePublishableKey],
  );
  const options = useMemo(() => ({ clientSecret }), [clientSecret]);

  if (!clientSecret || !stripePromise) return null;

  // key on the client secret: Stripe treats options.clientSecret as immutable
  // once mounted, so a changed secret needs a fresh provider (otherwise Stripe
  // logs "Unsupported prop change" and keeps confirming the stale PaymentIntent).
  return (
    <Elements key={clientSecret} stripe={stripePromise} options={options}>
      <StripePaymentFields {...fieldProps} />
    </Elements>
  );
}
